import React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon
} from '@mui/icons-material';

const PhotoPreviewModal = ({ open, onClose, photo, onShare, brandedContentSettings, brandedContentImage }) => {
  if (!photo) return null;

  const handleShare = () => {
    onShare(photo);
    onClose();
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'auto',
          height: 'auto',
          maxWidth: '90vw',
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        {/* Header with title and buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              mr: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {photo.key?.split('/').pop()}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Add to email">
              <IconButton
                onClick={handleShare}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'white'
                  }
                }}
              >
                <SendIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Close">
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Image content */}
        <DialogContent sx={{ 
          p: 0, 
          flexGrow: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={photo.url}
              alt={photo.name || photo.key?.split('/').pop()}
              sx={{
                maxWidth: 'calc(90vw - 32px)', // Account for dialog padding
                maxHeight: 'calc(80vh - 80px)', // Account for header height
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
            
            {/* Branded Logo Overlay */}
            {brandedContentImage && brandedContentSettings && (() => {
              // Calculate proportional logo size for preview modal
              // The preview modal shows larger images, so we need to scale accordingly
              const previewScaleFactor = 0.15; // Scale factor for preview modal
              const scaledLogoWidth = Math.max(brandedContentSettings.width * previewScaleFactor, 30); // Minimum 30px
              
              return (
                <Box
                  component="img"
                  src={brandedContentImage}
                  alt="Brand logo overlay"
                  sx={{
                    position: 'absolute',
                    width: `${scaledLogoWidth}px`,
                    height: 'auto',
                    maxWidth: '15%',
                    maxHeight: '15%',
                    opacity: 0.8,
                    ...(brandedContentSettings.position === 'upper-left' && {
                      top: '20px',
                      left: '20px',
                    }),
                    ...(brandedContentSettings.position === 'upper-right' && {
                      top: '20px',
                      right: '20px',
                    }),
                    ...(brandedContentSettings.position === 'lower-left' && {
                      bottom: '20px',
                      left: '20px',
                    }),
                    ...(brandedContentSettings.position === 'lower-right' && {
                      bottom: '20px',
                      right: '20px',
                    }),
                  }}
                />
              );
            })()}
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default PhotoPreviewModal; 